import { defineStore } from "pinia"
import { ref, watch } from "vue"
import { createDiscreteApi } from "naive-ui"
import type { Order } from "@/order/model/OrderModel"
import notificationSound from "@/assets/audio/tone.mp3"
import { useOrganizationStore } from "@/store/OrganizationStore"
import { listActive } from "@/order/api/vendor/V2OrderApi"
import dayjs from "dayjs"
import message from "@/util/message"

const STORAGE_KEY_PREFIX = "vendor_order_notification"
const STORAGE_MESSAGES_KEY = "order_notification_messages"
const POLL_INTERVAL = 5 * 60 * 1000

export interface OrderNotificationMessage {
  id: string
  vendorId: string
  vendorName: string
  schoolName: string
  deliveryDate: number
  createdAt: number
}

interface VendorNotificationState {
  vendorId: string
  isListening: boolean
  knownOrderIds: Set<string>
  timer: number | null
}

export const useOrderNotificationStore = defineStore("orderNotification", () => {
  const organizationStore = useOrganizationStore()

  let notification: ReturnType<typeof createDiscreteApi>["notification"] | null = null
  const getNotification = () => {
    if (!notification) {
      const api = createDiscreteApi(["notification"])
      notification = api.notification
    }
    return notification
  }

  const vendorStates = ref<Map<string, VendorNotificationState>>(new Map())
  const messages = ref<OrderNotificationMessage[]>([])
  let audio: HTMLAudioElement | null = null

  const loadMessages = () => {
    const savedMessages = localStorage.getItem(STORAGE_MESSAGES_KEY)
    messages.value = savedMessages ? JSON.parse(savedMessages) : []
  }

  const saveMessages = () => {
    localStorage.setItem(STORAGE_MESSAGES_KEY, JSON.stringify(messages.value))
  }

  // 监听消息变化，自动保存
  watch(
    () => messages.value,
    () => {
      saveMessages()
    },
    { deep: true },
  )

  const getOrCreateVendorState = (vendorId: string): VendorNotificationState => {
    if (!vendorStates.value.has(vendorId)) {
      vendorStates.value.set(vendorId, {
        vendorId,
        isListening: false,
        knownOrderIds: new Set(),
        timer: null,
      })
    }
    return vendorStates.value.get(vendorId)!
  }

  // 播放提示音
  const playNotificationSound = () => {
    try {
      if (!audio) {
        audio = new Audio(notificationSound)
      }
      audio.play().catch((error) => {
        console.warn("音频播放失败:", error.message)
      })
    } catch (error) {
      console.error("音频初始化失败:", error)
    }
  }

  // 添加新订单通知到列表
  const addOrderMessages = async (newOrders: Order[], vendorId: string) => {
    const vendorName = await organizationStore.getName(vendorId)
    const newMessages: OrderNotificationMessage[] = newOrders.map((order) => ({
      id: order.id,
      vendorId,
      vendorName,
      schoolName: order.buyerName,
      deliveryDate: order.requestedDeliveryTime,
      createdAt: Date.now(),
    }))
    messages.value = [...newMessages, ...messages.value]
  }

  // 删除单条通知
  const removeMessage = (id: string) => {
    messages.value = messages.value.filter((m) => m.id !== id)
  }

  // 清空某个供应商的所有通知
  const clearVendorMessages = (vendorId: string) => {
    messages.value = messages.value.filter((m) => m.vendorId !== vendorId)
  }

  // 清空所有通知
  const clearAllMessages = () => {
    messages.value = []
  }

  // 获取未来3天的订单
  const getFuture3DaysOrders = async (vendorId: string): Promise<Order[]> => {
    const today = dayjs().startOf("day").valueOf()
    const dayAfterTomorrow = dayjs().add(2, "day").endOf("day").valueOf()

    const result = await listActive({
      vendorId: vendorId,
      requestedDeliveryTime: [today, dayAfterTomorrow],
      schoolIds: [],
      cafeteriaIds: [],
      states: [],
      page: 1,
      pageSize: 1000,
    })

    return result.result
  }

  // 后台刷新订单
  const silentReload = async (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (!state || !state.isListening) return

    try {
      const future3DaysOrders = await getFuture3DaysOrders(vendorId)
      const currentOrderIds = new Set(future3DaysOrders.map((order) => order.id))
      const newOrders = future3DaysOrders.filter((order) => !state.knownOrderIds.has(order.id))

      if (newOrders.length > 0) {
        playNotificationSound()
        await addOrderMessages(newOrders, vendorId)
      }

      state.knownOrderIds = currentOrderIds
      saveState(vendorId)
    } catch (error: any) {
      message.networkError(error)
    }
  }

  // 启动定时器
  const startTimer = (vendorId: string) => {
    const state = getOrCreateVendorState(vendorId)

    if (state.timer) {
      clearInterval(state.timer)
    }

    state.timer = window.setInterval(() => {
      silentReload(vendorId)
    }, POLL_INTERVAL)
  }

  // 停止定时器
  const stopTimer = (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (state?.timer) {
      clearInterval(state.timer)
      state.timer = null
    }
  }

  // 保存状态到 localStorage
  const saveState = (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (state?.isListening) {
      const storageKey = `${STORAGE_KEY_PREFIX}_${vendorId}`
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          isListening: true,
          knownOrderIds: Array.from(state.knownOrderIds),
          lastUpdateTime: Date.now(),
        }),
      )
    }
  }

  // 清除状态
  const clearState = (vendorId: string) => {
    const storageKey = `${STORAGE_KEY_PREFIX}_${vendorId}`
    localStorage.removeItem(storageKey)
  }

  // 恢复状态（单个供应商）
  const restoreState = (vendorId: string): boolean => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}_${vendorId}`
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const savedData = JSON.parse(saved)
        if (savedData.isListening) {
          const state = getOrCreateVendorState(vendorId)
          state.knownOrderIds = new Set(savedData.knownOrderIds || [])
          return true
        }
      }
    } catch (error) {
      console.error(`恢复供应商${vendorId}状态失败:`, error)
    }
    return false
  }

  // 开启提醒（单个供应商）
  const startListening = async (vendorId: string) => {
    const state = getOrCreateVendorState(vendorId)
    state.isListening = true
    try {
      const future3DaysOrders = await getFuture3DaysOrders(vendorId)
      state.knownOrderIds = new Set(future3DaysOrders.map((order) => order.id))
    } catch (error) {
      state.knownOrderIds = new Set()
    }

    saveState(vendorId)
    startTimer(vendorId)

    getNotification().success({
      title: "开启新订单提醒",
      content: "每5分钟自动检查未来3天的新订单",
      duration: 3000,
    })
  }

  // 关闭提醒（单个供应商）
  const stopListening = (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (state) {
      state.isListening = false
      state.knownOrderIds.clear()
      stopTimer(vendorId)
      clearState(vendorId)
      clearVendorMessages(vendorId)
    }

    getNotification().info({
      title: "关闭新订单提醒",
      duration: 2000,
    })
  }

  // 关闭所有供应商提醒
  const stopAllListening = () => {
    vendorStates.value.forEach((_, vendorId) => {
      const state = vendorStates.value.get(vendorId)
      if (state) {
        state.isListening = false
        state.knownOrderIds.clear()
        stopTimer(vendorId)
        clearState(vendorId)
        clearVendorMessages(vendorId)
      }
    })
    vendorStates.value.clear()
    getNotification().info({
      title: "关闭新订单提醒",
      duration: 2000,
    })
  }

  // 销毁所有定时器并清除 localStorage 中的全部监听状态（退出登录时调用）
  const destroyAll = () => {
    vendorStates.value.forEach((state, vendorId) => {
      stopTimer(vendorId)
    })
    vendorStates.value.clear()

    // 清除所有供应商的监听状态
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  }

  // 切换提醒状态（单个供应商）
  const toggleListening = async (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (state?.isListening) {
      stopListening(vendorId)
    } else {
      await startListening(vendorId)
    }
  }

  // 手动刷新（单个供应商）
  const manualRefresh = async (vendorId: string) => {
    const state = vendorStates.value.get(vendorId)
    if (!state) return

    if (state.isListening) {
      try {
        const future3DaysOrders = await getFuture3DaysOrders(vendorId)
        state.knownOrderIds = new Set(future3DaysOrders.map((order) => order.id))
        saveState(vendorId)
      } catch (error) {
        console.error(`手动刷新供应商${vendorId}订单失败:`, error)
      }
    }
  }

  // 初始化单个供应商（应用启动或页面进入时调用）
  const initialize = (vendorId: string): boolean => {
    if (restoreState(vendorId)) {
      const state = getOrCreateVendorState(vendorId)
      if (state.timer) {
        clearInterval(state.timer)
        state.timer = null
      }
      state.isListening = true
      startTimer(vendorId)
      return true
    }
    return false
  }

  // 初始化所有供应商（应用启动时调用）
  const initializeAll = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        const vendorId = key.replace(`${STORAGE_KEY_PREFIX}_`, "")
        initialize(vendorId)
      }
    }
    loadMessages()
  }

  // 清理所有定时器（应用卸载时调用）
  const cleanupAll = () => {
    vendorStates.value.forEach((state, vendorId) => {
      stopTimer(vendorId)
    })
    vendorStates.value.clear()
  }

  // 判断某个供应商是否正在监听
  const isVendorListening = (vendorId: string): boolean => {
    return vendorStates.value.get(vendorId)?.isListening ?? false
  }

  const hasAnyListening = (): boolean => {
    return Array.from(vendorStates.value.values()).some((state) => state.isListening)
  }

  return {
    isVendorListening,
    messages,
    removeMessage,
    clearVendorMessages,
    clearAllMessages,
    startListening,
    stopListening,
    toggleListening,
    manualRefresh,
    initialize,
    initializeAll,
    cleanupAll,
    stopAllListening,
    destroyAll,
    hasAnyListening,
  }
})
